import { useCallback, useRef, useState } from 'react';
import { difference } from 'lodash';
import { syncedMeasurements } from '../../components/measurementsStore/elementMeasurements/syncedMeasurementsSingleton';
import { ElementMeasurement } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';
import { DEBUG_ELEMENT_PLACEHOLDER_STATE_UPDATE } from '../../debug/elementPlaceholders/PlaceholderDebugToolConstants';
import { useDispatch, useSelector } from 'react-redux';
import { isDebugToolbarToolEnabled } from '../../debug/debugStateSelector';
import { DebugTools } from '../../debug/store/debugToolTypes';
import useDebouncedCallback from '../../utils/react/useDebouncedCallback';
import { ElementPlaceholderState } from '../../debug/elementPlaceholders/ElementPlaceholdersDebugToolPopup';

const DEFAULT_INITIAL_RENDER_SIZE = 20;

export type ListPlaceholdersProps = {
    saveCurrentListPlaceholderState: () => void;
    generateInitialElementsVisibility: (childIds: string[], defaultVisibleCount?: number) => void;
    getListInitialRenderSize: (childIds: string[], defaultRenderSize?: number) => number;
    allowElementPlaceholder: boolean;
    onElementVisibilityChange: (elementId: string, isVisible: boolean) => void;
    savedPlaceholderMeasurementsRef: React.MutableRefObject<{ [a: string]: ElementMeasurement } | null>;
    initialElementsVisibilityMap: Map<string, boolean> | null;
    updateDebugStateIfToolEnabled?: (elementId: string, elementPlaceholderState: any) => void;
    removeFromDebugStateIfToolEnabled?: (elementId: string) => void;
};

/**
 * Adds list placeholder functionality to a component.
 *
 * Keeps track of the element measurements in the list so that placeholders can be
 * used on following renders, and calculates the initial render size based on how many
 * placeholders can be rendered.
 *
 * If retainPreviousVisibleElements is true, it will also keep track of the visibility of elements
 * on close so that the same ones will be visible when the list is opened again.
 */
const useElementListPlaceholdersState = (retainPreviousVisibleElements = false): ListPlaceholdersProps => {
    const currentElementVisibilityRef = useRef(new Map());
    const prevElementsVisibilityRef = useRef(new Map());
    const [initialElementsVisibilityMap, setInitialElementsVisibilityMap] = useState(new Map());
    const savedPlaceholderMeasurementsRef = useRef<{ [a: string]: ElementMeasurement } | null>(null);

    /**
     * Save current state for the next time inbox is opened
     * This must be run BEFORE the content is removed so that measurements are still available
     */
    const saveCurrentListPlaceholderState = useCallback(() => {
        savedPlaceholderMeasurementsRef.current = { ...syncedMeasurements };
        if (!retainPreviousVisibleElements) return;

        prevElementsVisibilityRef.current = new Map(currentElementVisibilityRef.current);
    }, []);

    const onElementVisibilityChange = useCallback((elementId: string, isVisible: boolean) => {
        if (!retainPreviousVisibleElements) return;

        currentElementVisibilityRef.current.set(elementId, isVisible);
    }, []);

    /**
     * Check if we should add previously loaded elements to the initial render count, so we can continue the batched
     * render instead of restarting, since we likely have the measurements to load placeholders for most of them
     * It seems to work best if we just use this for lists where we retain previous visible elements
     */
    const getListInitialRenderSize = useCallback(
        (childIds: string[], defaultRenderSize = DEFAULT_INITIAL_RENDER_SIZE) => {
            if (!savedPlaceholderMeasurementsRef.current) return defaultRenderSize;

            const preMeasuredElements = childIds?.filter(
                (elementId) => savedPlaceholderMeasurementsRef.current?.[elementId],
            );
            const preMeasuredElementsCount = preMeasuredElements.length || 0;
            if (preMeasuredElementsCount === 0) return defaultRenderSize;

            const potentialFirstBatch = childIds.slice(0, preMeasuredElementsCount);
            const missingMeasurements = difference(potentialFirstBatch, preMeasuredElements);

            // Only use this number if there are enough measurements to render placeholders for most elements.
            // There may be some missing if new elements have been added since last render
            if (missingMeasurements.length < defaultRenderSize) {
                return preMeasuredElementsCount;
            }

            return defaultRenderSize;
        },
        [],
    );

    /**
     * calculate the initial visibility of elements (before the ElementVisibilityObserver has rendered)
     * based on the current child ids and the previous state
     */
    const generateInitialElementsVisibility = useCallback(
        (childIds: string[], defaultVisibleCount = DEFAULT_INITIAL_RENDER_SIZE) => {
            let newElementsVisibilityMap = new Map();

            if (retainPreviousVisibleElements && prevElementsVisibilityRef.current) {
                newElementsVisibilityMap = prevElementsVisibilityRef.current;
            } else {
                childIds.forEach((elementId, index) => {
                    newElementsVisibilityMap.set(elementId, index < defaultVisibleCount);
                });
            }

            setInitialElementsVisibilityMap(newElementsVisibilityMap);
        },
        [],
    );

    // ------ Debugging ------

    const latestDebugMapRef = useRef(new Map());
    const isDebugToolEnabled = useSelector(isDebugToolbarToolEnabled(DebugTools.ELEMENT_PLACEHOLDERS));
    const dispatch = useDispatch();
    const updateDebugToolState = () => {
        dispatch({
            type: DEBUG_ELEMENT_PLACEHOLDER_STATE_UPDATE,
            payload: latestDebugMapRef.current,
        });
    };

    const debouncedUpdateDebugState = useDebouncedCallback(updateDebugToolState, 10, []);

    const updateDebugState = useCallback((elementId: string, elementPlaceholderState: ElementPlaceholderState) => {
        latestDebugMapRef.current.set(elementId, elementPlaceholderState);
        debouncedUpdateDebugState();
    }, []);

    const removeFromDebugState = useCallback((elementId: string) => {
        latestDebugMapRef.current.delete(elementId);
        debouncedUpdateDebugState();
    }, []);

    return {
        // For use in useElementListPlaceholdersInitialisation
        getListInitialRenderSize,
        saveCurrentListPlaceholderState,
        generateInitialElementsVisibility,

        // For use in useElementPlaceholderState
        allowElementPlaceholder: true,
        onElementVisibilityChange,
        savedPlaceholderMeasurementsRef,
        initialElementsVisibilityMap,
        updateDebugStateIfToolEnabled: isDebugToolEnabled ? updateDebugState : undefined,
        removeFromDebugStateIfToolEnabled: isDebugToolEnabled ? removeFromDebugState : undefined,
    };
};

export default useElementListPlaceholdersState;
